import {inject, Injectable} from '@angular/core';
import {ApiResponse, ApiResponsePageable, PageControl} from '../interfaces/application';
import {HttpClient} from '@angular/common/http';
import {buildHttpParams, extractCollection} from '../shared/utils';
import {BehaviorSubject, finalize, map, Observable} from 'rxjs';
import {environment} from '../../environment/environment';

@Injectable({
  providedIn: 'root'
})
export abstract  class CrudService<T, R = T, S = T> {

  protected readonly _http: HttpClient = inject(HttpClient);

  protected readonly _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  protected constructor(
    protected readonly path: string
  ) {}

  protected toggleLoading(): void {
    this._loading.next(!this._loading.value);
  }

  private _defaultMeta(pageControl: PageControl): ApiResponsePageable<T>['meta'] {
    return {
      current_page: pageControl.page || 0,
      per_page: pageControl.take || pageControl.per_page || 0,
      path: `${environment.api}/${this.path}`,
      from: 0,
      to: 0,
      total: 0,
      last_page: 0,
      first_page_url: '',
      next_page_url: '',
      prev_page_url: '',
      last_page_url: '',
    };
  }

  public loading(): Observable<boolean> {
    return this._loading.asObservable();
  }

  public get(pageControl: PageControl = {take: 0}, filters?: any): Observable<ApiResponsePageable<T>> {
    this.toggleLoading();
    const params = buildHttpParams({ ...pageControl, ...filters });
    return this._http.get<any>(`${environment.api}/${this.path}`, { params })
      .pipe(
        map((response) => ({
          ...response,
          data: extractCollection<T>(response?.data),
          meta: response?.meta ?? response?.data?.meta ?? this._defaultMeta(pageControl),
          extra: response?.extra ?? response?.data?.extra,
        }) as ApiResponsePageable<T>),
        finalize(() => this.toggleLoading())
      );
  }

  public show(id: string): Observable<ApiResponse<T>> {
    this.toggleLoading();
    return this._http.get<ApiResponse<T>>(`${environment.api}/${this.path}/${id}`)
      .pipe(finalize(() => this.toggleLoading()));
  }

  public post(payload: R): Observable<ApiResponse<T>> {
    this.toggleLoading();
    return this._http.post<ApiResponse<T>>(`${environment.api}/${this.path}`, payload)
      .pipe(finalize(() => this.toggleLoading()));
  }

  public put(id: string, payload: S): Observable<ApiResponse<T>> {
    this.toggleLoading();
    return this._http.put<ApiResponse<T>>(`${environment.api}/${this.path}/${id}`, payload)
      .pipe(finalize(() => this.toggleLoading()));
  }

  public delete(id: string): Observable<void> {
    this.toggleLoading();
    return this._http.delete<void>(`${environment.api}/${this.path}/${id}`)
      .pipe(finalize(() => this.toggleLoading()));
  }
}
